import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { X, QrCode, MapPin, CheckCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import SessionPickerModal from './SessionPickerModal';

// ── Types ────────────────────────────────────────────────────────────────────
type ScreenState =
  | 'scanning'
  | 'checking_in'
  | 'success'
  | 'already_checked_in'
  | 'no_session'
  | 'gps_error'
  | 'error'
  | 'invalid_qr'
  | 'permission_denied';

interface SessionOption {
  _id: string;
  formula: string;
  startTime: string;
  endTime: string;
  groupName?: string;
}

interface QRScannerModalProps {
  onClose: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function QRScannerModal({ onClose }: QRScannerModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [screenState, setScreenState] = useState<ScreenState>('scanning');
  const [errorMsg, setErrorMsg] = useState('');
  const [successStatus, setSuccessStatus] = useState<'present' | 'late'>('present');
  const [sessions, setSessions] = useState<SessionOption[]>([]);
  const [pendingCoords, setPendingCoords] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [showPicker, setShowPicker] = useState(false);

  const hasScanned = useRef(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerStarted = useRef(false);

  // ── Start camera ─────────────────────────────────────────────────────────
  useEffect(() => {
    const scanner = new Html5Qrcode('qr-reader');
    scannerRef.current = scanner;

    const start = async () => {
      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1 },
          handleScan,
          () => undefined
        );
        scannerStarted.current = true;
      } catch {
        setScreenState('permission_denied');
      }
    };

    start();

    return () => {
      if (scannerStarted.current) {
        scannerRef.current?.stop().catch(() => null);
      }
    };
  }, []);

  // ── Check-in mutation ────────────────────────────────────────────────────
  const checkInMutation = useMutation({
    mutationFn: (body: { branchId: string; latitude: number; longitude: number }) =>
      api.post('/student/attendance/check-in', body).then((r) => r.data?.data),
    onSuccess: (data) => {
      if (data?.requiresSelection && data?.sessions) {
        setSessions(data.sessions);
        setPendingCoords(
          checkInMutation.variables
            ? {
                latitude: checkInMutation.variables.latitude,
                longitude: checkInMutation.variables.longitude,
              }
            : null
        );
        setShowPicker(true);
        setScreenState('scanning');
        return;
      }
      setSuccessStatus(data?.status === 'present' ? 'present' : 'late');
      setScreenState('success');
      queryClient.invalidateQueries({ queryKey: ['student-attendance'] });
      queryClient.invalidateQueries({ queryKey: ['student-attendance-summary'] });
    },
    onError: (err: any) => {
      hasScanned.current = false;
      const status = err?.response?.status;
      const raw = err?.response?.data?.message || err?.message || t('common.error');
      const message = Array.isArray(raw) ? raw.join('\n') : raw;

      if (status === 409) {
        setScreenState('already_checked_in');
        return;
      }
      if (status === 404) {
        setErrorMsg(message);
        setScreenState('no_session');
        return;
      }
      if (status === 403) {
        let finalMessage = message;
        if (message.includes('. Device ')) {
          const [arPart, enPart] = message.split('. Device ');
          finalMessage = arPart ? `${arPart}.` : `Device ${enPart}`;
        }
        setErrorMsg(finalMessage);
        setScreenState('gps_error');
        return;
      }
      setErrorMsg(message);
      setScreenState('error');
    },
  });

  // ── QR + GPS handler ─────────────────────────────────────────────────────
  const handleScan = async (data: string) => {
    if (hasScanned.current) return;
    hasScanned.current = true;
    setScreenState('checking_in');

    // Stop scanner immediately after scan
    if (scannerStarted.current) {
      await scannerRef.current?.stop().catch(() => null);
      scannerStarted.current = false;
    }

    // Parse QR
    let branchId: string;
    try {
      const parsed = JSON.parse(data);
      branchId = parsed?.branchId ?? data;
    } catch {
      branchId = data;
    }

    const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(branchId);
    if (!branchId || !isValidObjectId) {
      setScreenState('invalid_qr');
      return;
    }

    // Get GPS
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
        })
      );
      const { latitude, longitude } = pos.coords;
      checkInMutation.mutate({ branchId, latitude, longitude });
    } catch {
      setErrorMsg(
        t('student.attendance.gpsError', { defaultValue: 'تعذّر تحديد موقعك. تأكد من تفعيل GPS.' })
      );
      setScreenState('gps_error');
    }
  };

  const handleScanAgain = async () => {
    hasScanned.current = false;
    setErrorMsg('');
    setScreenState('scanning');

    // Restart scanner
    if (!scannerStarted.current && scannerRef.current) {
      try {
        await scannerRef.current.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1 },
          handleScan,
          () => undefined
        );
        scannerStarted.current = true;
      } catch {
        setScreenState('permission_denied');
      }
    }
  };

  // ── Session picker success ────────────────────────────────────────────────
  const handlePickerSuccess = (status: 'present' | 'late') => {
    setShowPicker(false);
    setSuccessStatus(status);
    setScreenState('success');
    queryClient.invalidateQueries({ queryKey: ['student-attendance'] });
    queryClient.invalidateQueries({ queryKey: ['student-attendance-summary'] });
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Session picker overlay */}
      {showPicker && pendingCoords && (
        <SessionPickerModal
          sessions={sessions}
          latitude={pendingCoords.latitude}
          longitude={pendingCoords.longitude}
          onSuccess={handlePickerSuccess}
          onClose={() => {
            setShowPicker(false);
            hasScanned.current = false;
          }}
        />
      )}

      {/* Main Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
        <div className="relative w-full max-w-sm bg-card border border-border/70 rounded-[28px] overflow-hidden shadow-2xl">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 end-3 z-40 flex items-center justify-center w-9 h-9 rounded-full bg-white/10 border border-white/15 text-white backdrop-blur-sm hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* ── SCANNING STATE ── */}
          {(screenState === 'scanning' || screenState === 'checking_in') && (
            <div className="relative bg-black overflow-hidden">
              {/* Camera viewport */}
              <div id="qr-reader" className="w-full" style={{ minHeight: 380 }} />

              {/* Dark vignette overlay with 4-way square cutout */}
              <div className="absolute inset-0 pointer-events-none z-10">
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  style={{ marginTop: -20 }}
                >
                  <div className="relative w-[260px] h-[260px] rounded-[22px]">
                    {/* Dark panels around the square */}
                    <div className="absolute -top-[100vh] -left-[100vw] -right-[100vw] h-[100vh] bg-black/65" />
                    <div className="absolute -bottom-[100vh] -left-[100vw] -right-[100vw] h-[100vh] bg-black/65" />
                    <div className="absolute top-0 bottom-0 -left-[100vw] w-[100vw] bg-black/65" />
                    <div className="absolute top-0 bottom-0 -right-[100vw] w-[100vw] bg-black/65" />
                  </div>
                </div>
              </div>

              {/* Animated scan frame */}
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{ marginTop: -20 }}
              >
                <div className="relative w-[260px] h-[260px]">
                  {/* Corner lines */}
                  {['tl', 'tr', 'bl', 'br'].map((pos) => (
                    <div
                      key={pos}
                      className="absolute w-8 h-8 animate-pulse"
                      style={{
                        borderWidth: 0,
                        borderColor: 'hsl(var(--primary))',
                        ...(pos === 'tl'
                          ? {
                              top: 0,
                              left: 0,
                              borderTopWidth: 3.5,
                              borderLeftWidth: 3.5,
                              borderTopLeftRadius: 8,
                            }
                          : {}),
                        ...(pos === 'tr'
                          ? {
                              top: 0,
                              right: 0,
                              borderTopWidth: 3.5,
                              borderRightWidth: 3.5,
                              borderTopRightRadius: 8,
                            }
                          : {}),
                        ...(pos === 'bl'
                          ? {
                              bottom: 0,
                              left: 0,
                              borderBottomWidth: 3.5,
                              borderLeftWidth: 3.5,
                              borderBottomLeftRadius: 8,
                            }
                          : {}),
                        ...(pos === 'br'
                          ? {
                              bottom: 0,
                              right: 0,
                              borderBottomWidth: 3.5,
                              borderRightWidth: 3.5,
                              borderBottomRightRadius: 8,
                            }
                          : {}),
                      }}
                    />
                  ))}

                  {/* Animated scan line */}
                  <div
                    className="absolute left-0 right-0 h-0.5 rounded-full shadow-[0_0_8px_2px_hsla(var(--primary),0.5)] animate-[scanline_2s_ease-in-out_infinite]"
                    style={{ top: '50%', backgroundColor: 'hsl(var(--primary))' }}
                  />
                </div>
              </div>

              {/* Top hint pill */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-black/60 border border-white/10 backdrop-blur-md z-30">
                <QrCode className="w-4 h-4 text-white" />
                <span className="text-[11px] text-white font-bold whitespace-nowrap">
                  {screenState === 'checking_in'
                    ? t('student.attendance.checkingIn', { defaultValue: 'جاري تسجيل الحضور...' })
                    : t('student.attendance.scanQrHint', {
                        defaultValue: 'وجّه الكاميرا على QR Code الفرع',
                      })}
                </span>
              </div>

              {/* Bottom bar */}
              <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-2 pb-6 pt-5 bg-black/55 border-t border-white/8 rounded-t-[20px]">
                <div
                  className="flex items-center justify-center w-12 h-12 rounded-full border border-primary/40"
                  style={{ backgroundColor: 'hsla(var(--primary), 0.15)' }}
                >
                  {screenState === 'checking_in' ? (
                    <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  ) : (
                    <QrCode className="w-6 h-6 text-white" />
                  )}
                </div>
                <span className="text-[14px] font-bold text-white">
                  {t('student.attendance.scanQrTitle', { defaultValue: 'مسح QR Code' })}
                </span>
                <span className="text-[11px] text-white/50">
                  {t('student.attendance.scanQrSub', {
                    defaultValue: 'امسح الكود الموجود داخل الفرع',
                  })}
                </span>
              </div>
            </div>
          )}

          {/* ── RESULT STATES ── */}
          {screenState !== 'scanning' && screenState !== 'checking_in' && (
            <div className="p-7 flex flex-col items-center gap-4">
              <ResultContent
                state={screenState}
                successStatus={successStatus}
                errorMsg={errorMsg}
                t={t}
                onScanAgain={handleScanAgain}
                onClose={onClose}
              />
            </div>
          )}
        </div>
      </div>

      {/* CSS for scan animation and hiding library UI */}
      <style>{`
        @keyframes scanline {
          0%   { top: 2px;  opacity: 1; }
          50%  { top: calc(100% - 2px); opacity: 1; }
          100% { top: 2px;  opacity: 1; }
        }
        #qr-reader img { display: none !important; }
        #qr-reader { border: none !important; }
        #qr-reader * { border: none !important; box-shadow: none !important; }
        #qr-reader__scan_region { display: flex; justify-content: center; overflow: hidden; }
        #qr-reader video { object-fit: cover !important; display: block !important; }
      `}</style>
    </>
  );
}

// ── Result Content Component ──────────────────────────────────────────────────
function ResultContent({
  state,
  successStatus,
  errorMsg,
  t,
  onScanAgain,
  onClose,
}: {
  state: ScreenState;
  successStatus: 'present' | 'late';
  errorMsg: string;
  t: any;
  onScanAgain: () => void;
  onClose: () => void;
}) {
  const configs: Record<
    string,
    {
      icon: any;
      iconBg: string;
      iconColor: string;
      badge: string;
      badgeBg: string;
      title: string;
      desc: string;
    }
  > = {
    success: {
      icon: successStatus === 'present' ? CheckCircle : Clock,
      iconBg: successStatus === 'present' ? 'bg-emerald-500/15' : 'bg-amber-500/15',
      iconColor: successStatus === 'present' ? 'text-emerald-500' : 'text-amber-500',
      badge:
        successStatus === 'present'
          ? t('student.attendance.present', { defaultValue: 'حاضر' })
          : t('student.attendance.late', { defaultValue: 'متأخر' }),
      badgeBg:
        successStatus === 'present'
          ? 'bg-emerald-500/10 text-emerald-600'
          : 'bg-amber-500/10 text-amber-600',
      title:
        successStatus === 'present'
          ? t('student.attendance.checkInSuccess', { defaultValue: 'تم تسجيل حضورك بنجاح!' })
          : t('student.attendance.checkInLate', { defaultValue: 'تم التسجيل (متأخر)' }),
      desc:
        successStatus === 'present'
          ? t('student.attendance.checkInSuccessDesc', { defaultValue: 'تم تسجيل حضورك كـ حاضر.' })
          : t('student.attendance.checkInLateDesc', {
              defaultValue: 'تم التسجيل. لكن تأخرت عن موعد المحاضرة.',
            }),
    },
    already_checked_in: {
      icon: Clock,
      iconBg: 'bg-amber-500/15',
      iconColor: 'text-amber-500',
      badge: t('student.attendance.alreadyCheckedIn', { defaultValue: 'مسجّل مسبقاً' }),
      badgeBg: 'bg-amber-500/10 text-amber-600',
      title: t('student.attendance.alreadyCheckedInTitle', { defaultValue: 'سبق تسجيل حضورك' }),
      desc: t('student.attendance.alreadyCheckedInDesc', {
        defaultValue: 'لقد سجّلت حضورك في هذه المحاضرة من قبل.',
      }),
    },
    no_session: {
      icon: Clock,
      iconBg: 'bg-slate-500/10',
      iconColor: 'text-slate-400',
      badge: t('student.attendance.noSession', { defaultValue: 'لا توجد محاضرة' }),
      badgeBg: 'bg-slate-500/10 text-slate-500',
      title: t('student.attendance.noSessionTitle', { defaultValue: 'لا توجد محاضرة الآن' }),
      desc:
        errorMsg ||
        t('student.attendance.noSessionDesc', {
          defaultValue: 'لا توجد محاضرة نشطة لك في هذا الفرع حالياً.',
        }),
    },
    gps_error: {
      icon: MapPin,
      iconBg: 'bg-rose-500/15',
      iconColor: 'text-rose-500',
      badge: t('student.attendance.notAtBranch', { defaultValue: 'خارج الفرع' }),
      badgeBg: 'bg-rose-500/10 text-rose-600',
      title: t('student.attendance.notAtBranchTitle', { defaultValue: 'أنت بعيد عن الفرع' }),
      desc:
        errorMsg ||
        t('student.attendance.notAtBranchDesc', {
          defaultValue: 'يجب أن تكون داخل الفرع لتسجيل الحضور.',
        }),
    },
    invalid_qr: {
      icon: QrCode,
      iconBg: 'bg-slate-500/10',
      iconColor: 'text-slate-400',
      badge: t('student.attendance.invalidQr', { defaultValue: 'QR غير صالح' }),
      badgeBg: 'bg-slate-500/10 text-slate-500',
      title: t('student.attendance.invalidQr', { defaultValue: 'QR Code غير صالح' }),
      desc: t('student.attendance.invalidQrDesc', {
        defaultValue: 'هذا الـ QR Code غير مرتبط بأي فرع. جرّب مرة أخرى.',
      }),
    },
    error: {
      icon: AlertCircle,
      iconBg: 'bg-rose-500/15',
      iconColor: 'text-rose-500',
      badge: t('student.attendance.checkInFailed', { defaultValue: 'فشل التسجيل' }),
      badgeBg: 'bg-rose-500/10 text-rose-600',
      title: t('student.attendance.errorTitle', { defaultValue: 'حدث خطأ' }),
      desc: errorMsg,
    },
    permission_denied: {
      icon: QrCode,
      iconBg: 'bg-rose-500/15',
      iconColor: 'text-rose-500',
      badge: t('student.attendance.permissionDenied', { defaultValue: 'تم رفض الصلاحية' }),
      badgeBg: 'bg-rose-500/10 text-rose-600',
      title: t('student.attendance.cameraPermissionTitle', { defaultValue: 'الكاميرا محظورة' }),
      desc: t('student.attendance.cameraPermissionDesc', {
        defaultValue: 'اسمح للمتصفح بالوصول إلى الكاميرا والموقع من الإعدادات.',
      }),
    },
  };

  const cfg = configs[state];
  if (!cfg) return null;

  const Icon = cfg.icon;
  const showScanAgain = ['no_session', 'gps_error', 'invalid_qr', 'error'].includes(state);

  return (
    <>
      {/* Icon */}
      <div
        className={`flex items-center justify-center w-[90px] h-[90px] rounded-full ${cfg.iconBg} mb-1`}
      >
        <Icon className={`w-12 h-12 ${cfg.iconColor}`} strokeWidth={1.8} />
      </div>

      {/* Status Badge */}
      <div
        className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black ${cfg.badgeBg}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70 inline-block" />
        {cfg.badge}
      </div>

      {/* Title */}
      <h3 className="text-[18px] font-black text-foreground text-center leading-tight">
        {cfg.title}
      </h3>

      {/* Desc */}
      <p className="text-[12px] text-muted-foreground text-center leading-relaxed">{cfg.desc}</p>

      {/* Actions */}
      <div className="w-full flex flex-col gap-2.5 mt-1">
        {showScanAgain && (
          <button
            onClick={onScanAgain}
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-[14px] bg-primary hover:bg-primary/90 text-white text-[14px] font-black transition-colors shadow-md"
          >
            <RefreshCw className="w-4 h-4" />
            {t('student.attendance.scanAgain', { defaultValue: 'مسح مرة أخرى' })}
          </button>
        )}
        {state === 'success' && (
          <button
            onClick={onClose}
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-[14px] bg-primary hover:bg-primary/90 text-white text-[14px] font-black transition-colors shadow-md"
          >
            <CheckCircle className="w-4 h-4" />
            {t('student.attendance.done', { defaultValue: 'تم' })}
          </button>
        )}
        {state === 'already_checked_in' && (
          <button
            onClick={onClose}
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-[14px] bg-amber-500 hover:bg-amber-600 text-white text-[14px] font-black transition-colors"
          >
            {t('student.attendance.done', { defaultValue: 'تم' })}
          </button>
        )}
        <button
          onClick={onClose}
          className="flex items-center justify-center gap-1 w-full py-3 rounded-[14px] border border-border/80 text-muted-foreground text-[13px] font-bold hover:bg-muted/30 transition-colors"
        >
          {t('common.close', { defaultValue: 'إغلاق' })}
        </button>
      </div>
    </>
  );
}
